// react
import { useContext, useState } from 'react';

// router
import { useNavigate, useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextProject } from '../Project/Project.context';
import { ContextSpendPlan } from '../ProjectV2/ProjectV2.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// types
import { Category, Component, ExpenseType } from '../../../@types';

// mui
import { Box } from '@mui/material';

// icons
import {
	Category as IconCategory,
	Extension as IconComponent,
	Receipt as IconExpenseType,
	Settings as IconSettings,
} from '@mui/icons-material';

// v2 components
import {
	ActivityGroupedList,
	type GroupByKey,
} from '../../ui/ActivityGroupedList/ActivityGroupedList';
import { HeaderListingPage } from '../../ui/HeaderListingPage/HeaderListingPage';

// utils
import { getProjectLocalCurrency } from '../../../utils/localCurrency';

// components

// ui
import { BtnSplit } from '../../ui/BtnSplit/BtnSplit';

// dialogs
import { DialogAddComponent } from '../../dialogs/DialogAddComponent/DialogAddComponent';
import { DialogAddCategory } from '../../dialogs/DialogAddCategory/DialogAddCategory';
import { DialogAssignActivities } from '../../dialogs/DialogAssignActivities/DialogAssignActivities';
import { DialogAddExpenseType } from '../../dialogs/DialogAddExpenseType/DialogAddExpenseType';

// Component

export const PageComponentsCategories = () => {
	// hooks
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	// context
	const { project } = useContext(ContextProject);
	const { activities, getContractsByActivity } = useContext(ContextSpendPlan);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');
	const authHeaders = {
		Authorization: `Bearer ${accessToken}`,
		User: String(idToken),
	};

	// state
	const [componentsData, setComponentsData] = useState<Component[]>([]);
	const [categoriesData, setCategoriesData] = useState<Category[]>([]);
	const [expenseTypesData, setExpenseTypesData] = useState<ExpenseType[]>([]);
	const [assignTarget, setAssignTarget] = useState<{
		id: number;
		name: string;
		field: 'componentId' | 'categoryId' | 'expenseTypeId';
	} | null>(null);

	// fetch components
	const { refetch: refetchComponents } = useFetch({
		isEnabled: Boolean(project?.id),
		onSuccess: (res) => {
			if (res.data) setComponentsData(res.data);
		},
		options: { headers: authHeaders },
		url: `${process.env.REACT_APP_API_URL}/components?project=${project?.id}&orderBy=name&order=ASC`,
	});

	// fetch categories
	const { refetch: refetchCategories } = useFetch({
		isEnabled: Boolean(project?.id),
		onSuccess: (res) => {
			if (res.data) setCategoriesData(res.data);
		},
		options: { headers: authHeaders },
		url: `${process.env.REACT_APP_API_URL}/categories?project=${project?.id}&orderBy=name&order=ASC`,
	});

	// fetch expense types
	const { refetch: refetchExpenseTypes } = useFetch({
		isEnabled: Boolean(project?.id),
		onSuccess: (res) => {
			if (res.data) setExpenseTypesData(res.data);
		},
		options: { headers: authHeaders },
		url: `${process.env.REACT_APP_API_URL}/expense-types?project=${project?.id}&orderBy=name&order=ASC`,
	});

	// handlers

	const handleGoToSettings = () => {
		navigate(`/project/${project?.idWbg}/settings/components`);
	};

	const handleNavigateActivity = (activityId: number) => {
		navigate(`/project/${project?.idWbg}/spend-plans/activity/${activityId}`);
	};

	const handleNavigateContract = (contractId: number) => {
		navigate(`/project/${project?.idWbg}/spend-plans/contract/${contractId}`);
	};

	const handleAddActivity = (key: GroupByKey, itemId: number) => {
		const next = new URLSearchParams(searchParams);
		next.set('dialog', 'addActivity');
		if (key === 'component') next.set('dialogComponent', String(itemId));
		if (key === 'category') next.set('dialogCategory', String(itemId));
		if (key === 'expenseType') next.set('dialogExpenseType', String(itemId));
		setSearchParams(next);
	};

	const handleAssignExisting = (key: GroupByKey, itemId: number, itemName: string) => {
		const fieldMap: Record<string, 'componentId' | 'categoryId' | 'expenseTypeId'> = {
			component: 'componentId',
			category: 'categoryId',
			expenseType: 'expenseTypeId',
		};
		setAssignTarget({
			id: itemId,
			name: itemName,
			field: fieldMap[key] || 'componentId',
		});
	};

	const handleOpenDialog = (dialog: string) => {
		const next = new URLSearchParams(searchParams);
		next.set('dialog', dialog);
		setSearchParams(next);
	};

	const handleCloseDialog = () => {
		const next = new URLSearchParams(searchParams);
		next.delete('dialog');
		setSearchParams(next);
	};

	const paramDialog = searchParams.get('dialog');

	// track active tab for dynamic BtnSplit default
	const [activeTabKey, setActiveTabKey] = useState<GroupByKey>('component');

	return (
		<Box>
			{/* Header */}
			<HeaderListingPage
				icon={IconCategory}
				title={t('spendPlan.componentsAndCategories')}
				subtitle={t('spendPlan.componentsAndCategoriesSubtitle')}
				variant="activity"
				actions={
					<BtnSplit
						key={activeTabKey}
						actions={[
							// Tab-matching action comes first (becomes the default button)
							...(activeTabKey === 'component'
								? [
										{
											children: t('addComponent'),
											onClick: () => handleOpenDialog('addComponent'),
											startIcon: <IconComponent />,
										},
										{
											children: t('addCategory'),
											onClick: () => handleOpenDialog('addCategory'),
											startIcon: <IconCategory />,
										},
										{
											children: t('addExpenseType'),
											onClick: () => handleOpenDialog('addExpenseType'),
											startIcon: <IconExpenseType />,
										},
									]
								: activeTabKey === 'category'
									? [
											{
												children: t('addCategory'),
												onClick: () => handleOpenDialog('addCategory'),
												startIcon: <IconCategory />,
											},
											{
												children: t('addComponent'),
												onClick: () => handleOpenDialog('addComponent'),
												startIcon: <IconComponent />,
											},
											{
												children: t('addExpenseType'),
												onClick: () => handleOpenDialog('addExpenseType'),
												startIcon: <IconExpenseType />,
											},
										]
									: [
											{
												children: t('addExpenseType'),
												onClick: () => handleOpenDialog('addExpenseType'),
												startIcon: <IconExpenseType />,
											},
											{
												children: t('addComponent'),
												onClick: () => handleOpenDialog('addComponent'),
												startIcon: <IconComponent />,
											},
											{
												children: t('addCategory'),
												onClick: () => handleOpenDialog('addCategory'),
												startIcon: <IconCategory />,
											},
										]),
							{
								children: t('spendPlan.editInSettings'),
								onClick: () => handleGoToSettings(),
								startIcon: <IconSettings />,
							},
						]}
					/>
				}
			/>

			{/* Content — tabs, expand toggle, and grouped cards handled by ActivityGroupedList */}
			<ActivityGroupedList
				tabs={[
					{ key: 'component', label: t('components'), items: componentsData },
					{ key: 'category', label: t('categories'), items: categoriesData },
					{ key: 'expenseType', label: t('expenseTypes'), items: expenseTypesData },
				]}
				onTabChange={setActiveTabKey}
				onNavigateActivity={handleNavigateActivity}
				onNavigateContract={handleNavigateContract}
				onAddActivity={handleAddActivity}
				onAssignExisting={handleAssignExisting}
				getContractsByActivity={getContractsByActivity}
				projectLocalCurrency={getProjectLocalCurrency(project)}
			/>

			{/* Dialogs */}
			<DialogAddComponent
				isOpen={paramDialog === 'addComponent'}
				onClose={handleCloseDialog}
				onSuccess={() => {
					refetchComponents();
					handleCloseDialog();
				}}
			/>
			<DialogAddCategory
				isOpen={paramDialog === 'addCategory'}
				onClose={handleCloseDialog}
				onSuccess={() => {
					refetchCategories();
					handleCloseDialog();
				}}
			/>
			<DialogAddExpenseType
				isOpen={paramDialog === 'addExpenseType'}
				onClose={handleCloseDialog}
				onSuccess={() => {
					refetchExpenseTypes();
					handleCloseDialog();
				}}
			/>
			<DialogAssignActivities
				activities={activities}
				assignField={assignTarget?.field || 'componentId'}
				assignToId={assignTarget?.id || 0}
				assignToName={assignTarget?.name || ''}
				isOpen={Boolean(assignTarget)}
				onClose={() => setAssignTarget(null)}
				onSuccess={() => setAssignTarget(null)}
			/>
		</Box>
	);
};
